import { ListHeading } from "@/components/shared/list-header";
import { Cube, StackPlus } from "@/components/shared/icons";
import { View, Checkbox, Label } from "@/components/ui";
import { ServiceAddOnCard } from "@/components/shared/booking/service-add-on-card";
import { useId, useMemo } from "react";
import { useBookingState } from "@/store/booking/useBookingState";
import { PackageServiceTypeEnum } from "@/__generated__/graphql";
import {
  getHomeBeautyPackageFromCode,
  getHomeBeautyAddOnConfig,
} from "@/constants/booking";
import { formatPrice } from "@/lib/helpers/number";
import { showToast } from "@/components/ui/toast/show-toast";
import { Divider } from "@/components/shared/divider";
import { Typography } from "@/components/shared/typography";
import { ServiceCreditTopUpCard } from "../components/service-credit-topup-card";

const FreeGelRemovalCode = "HBNDFGR";

const AdditionalServiceCheckbox = ({
  duration,
  title,
  description,
  checked,
  error,
  onCheckChange,
}: {
  checked: boolean;
  onCheckChange(checked: boolean): void;
  duration: string;
  error?: string;
  title: string;
  description: string;
}) => {
  const id = useId();
  return (
    <>
      <View className="flex flex-row items-center gap-1">
        <Checkbox checked={checked} onCheckedChange={onCheckChange} id={id} />
        <View className="flex flex-1 flex-row">
          <Label htmlFor={id} className="flex-1 cursor-pointer">
            <Typography variant="body-sm" color="foreground-intermediate">
              {title}
            </Typography>
            <Typography variant="caption-md" color="foreground-intermediate">
              {description}
            </Typography>
          </Label>
        </View>
        <Typography variant="caption-md" color="foreground-intermediate">
          {duration}
        </Typography>
      </View>
      <Typography variant="caption-md" color="danger">
        {error}
      </Typography>
    </>
  );
};

export const HomeBeautyBooking = () => {
  const {
    data: { packageDetails, selectedPackageIds, homeBeautyFreeGelServiceIds },
    setBookingState,
  } = useBookingState();

  // Determine the selected main package and its add-on config
  const addOnConfig = useMemo(() => {
    if (!selectedPackageIds.length || !packageDetails.length) return null;
    const mainPkgId = selectedPackageIds[0];
    const mainPkg = packageDetails.find((pkg) => pkg.id === mainPkgId);
    if (!mainPkg) return null;
    return getHomeBeautyAddOnConfig(mainPkg.code);
  }, [selectedPackageIds, packageDetails]);

  const maxFreeGelUnits = addOnConfig?.maxFreeGelUnits ?? 0;

  const { upgradePackages, addonPackages } = useMemo(() => {
    const codeMappedPackages = packageDetails
      .map((pkg) => {
        const fePkg = getHomeBeautyPackageFromCode(pkg.code);
        return {
          ...fePkg,
          ...pkg,
          canIncludeFreeGel: false,
          maxItemLabel: undefined as string | undefined,
        };
      })
      .filter((pkg) => !pkg.undefinedCode);

    // Build a map of allowed add-on overrides from config
    const allowedAddOnMap = new Map(
      addOnConfig?.allowedAddOns.map((ao) => [ao.code, ao]) || [],
    );

    // Filter packages based on config (only upgrade/addon types are filtered)
    const filteredPackages = addOnConfig
      ? codeMappedPackages.filter((pkg) =>
          pkg.serviceType !== PackageServiceTypeEnum.Upgrade &&
          pkg.serviceType !== PackageServiceTypeEnum.AddOn
            ? true
            : allowedAddOnMap.has(pkg.code),
        )
      : codeMappedPackages;

    // Apply maxItem overrides and canIncludeFreeGel from config
    const overriddenPackages = filteredPackages.map((pkg) => {
      const override = allowedAddOnMap.get(pkg.code);
      if (override) {
        return {
          ...pkg,
          maxItem: override.maxItem,
          maxItemLabel: override.maxItemLabel,
          canIncludeFreeGel: override.canIncludeFreeGel ?? false,
        };
      }
      return pkg;
    });

    return {
      upgradePackages: overriddenPackages.filter(
        (pkg) => pkg.serviceType === PackageServiceTypeEnum.Upgrade,
      ),
      addonPackages: overriddenPackages
        .filter((pkg) => pkg.serviceType === PackageServiceTypeEnum.AddOn)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    };
  }, [packageDetails, addOnConfig]);

  const onAdd = (id: string) => {
    setBookingState({
      selectedPackageIds: [...selectedPackageIds, id],
    });
  };

  const onRemove = (id: string) => {
    selectedPackageIds.splice(
      selectedPackageIds.findIndex((pkgId) => pkgId === id),
      1,
    );
    setBookingState({
      selectedPackageIds,
    });
  };

  const onCheckChange = (checked: boolean, id: string) => {
    const freeGelRemovalId = packageDetails.find(
      (pkg) => pkg.code === FreeGelRemovalCode,
    )?.id as string;

    if (checked) {
      if (homeBeautyFreeGelServiceIds.length >= maxFreeGelUnits) {
        return showToast({
          title: `You can only have ${maxFreeGelUnits} Gel Removal within your booking`,
          type: "info",
        });
      }
      setBookingState({
        homeBeautyFreeGelServiceIds: [...homeBeautyFreeGelServiceIds, id],
        selectedPackageIds: [...selectedPackageIds, freeGelRemovalId],
      });
    } else {
      const index = selectedPackageIds.findIndex(
        (id) => id === freeGelRemovalId,
      );
      setBookingState({
        homeBeautyFreeGelServiceIds: homeBeautyFreeGelServiceIds.filter(
          (serviceId) => serviceId !== id,
        ),
        selectedPackageIds: selectedPackageIds.filter(
          (id, idx) => idx !== index,
        ),
      });
    }
  };

  return (
    <View className="flex flex-col gap-4">
      <View className="mx-4 mt-2">
        <ServiceCreditTopUpCard service="beauty" />
      </View>

      <Divider className="border-b-2" />

      {upgradePackages.length > 0 && (
        <View className="mx-4 flex flex-col pt-4">
          <ListHeading icon={Cube} title="Upgrade your nails" />
          <Typography
            variant="body-sm"
            color="foreground-intermediate"
            className="mb-6 mt-2"
          >
            Make your session more worth while and upgrade to enjoy our other
            nail service offerings.
          </Typography>
          <View className="flex flex-col gap-4">
            {upgradePackages.map((pkg) => {
              const checked = homeBeautyFreeGelServiceIds.includes(pkg.id);
              const shouldAddMoreItem =
                checked &&
                selectedPackageIds.filter((id) => id === pkg.id).length <
                  (pkg.minFreeGelAmount || 1000);
              return (
                <ServiceAddOnCard
                  key={pkg.id}
                  title={pkg.title}
                  subTitle={
                    formatPrice(pkg.serviceBillingValue) + pkg.suffix || ""
                  }
                  thirdTitle={`${60 * pkg.duration} mins`}
                  fourthTitle={
                    pkg.maxItem
                      ? `Max ${pkg.maxItem}${pkg.maxItemLabel ? ` (${pkg.maxItemLabel})` : ""}`
                      : undefined
                  }
                  description={pkg.description}
                  imgUrl={pkg.imgUrl}
                  slogan={pkg.mostPopular ? "Most Popular" : ""}
                  onAdd={() => onAdd(pkg.id)}
                  onMinus={() => onRemove(pkg.id)}
                  value={
                    selectedPackageIds.filter((id) => id === pkg.id).length
                  }
                  max={pkg.maxItem}
                  footer={
                    pkg.canIncludeFreeGel && maxFreeGelUnits > 0 ? (
                      <AdditionalServiceCheckbox
                        checked={checked}
                        onCheckChange={(checked) =>
                          onCheckChange(checked, pkg.id)
                        }
                        title="Include Free Gel Removal"
                        error={
                          shouldAddMoreItem
                            ? "Add more nails to get free gel removal"
                            : ""
                        }
                        description=""
                        duration="30 mins"
                      />
                    ) : undefined
                  }
                />
              );
            })}
          </View>
        </View>
      )}

      {upgradePackages.length > 0 && addonPackages.length > 0 && (
        <Divider className="my-2 border-b-2" />
      )}

      {addonPackages.length > 0 && (
        <View
          className={`mx-4 flex flex-col ${
            upgradePackages.length === 0 ? "pt-4" : ""
          }`}
        >
          <View className="mb-6 flex flex-col gap-2">
            <ListHeading icon={StackPlus} title="Treatment & Add-ons" />
            <Typography variant="body-sm" color="foreground-intermediate">
              Elevate your home beauty experience with our other available
              treatments and add-ons for the full self-care package.
            </Typography>
          </View>
          <View className="flex flex-col gap-4">
            {addonPackages.map((pkg) => {
              return (
                <ServiceAddOnCard
                  key={pkg.id}
                  title={pkg.title}
                  subTitle={`${formatPrice(pkg.serviceBillingValue)} ${pkg.suffix ?? ""}`}
                  thirdTitle={`${60 * pkg.duration} mins`}
                  fourthTitle={
                    pkg.maxItem
                      ? `Max ${pkg.maxItem}${pkg.maxItemLabel ? ` (${pkg.maxItemLabel})` : ""}`
                      : undefined
                  }
                  description={pkg.description}
                  imgUrl={pkg.imgUrl}
                  slogan={pkg.mostPopular ? "Most Popular" : ""}
                  onAdd={() => onAdd(pkg.id)}
                  onMinus={() => onRemove(pkg.id)}
                  value={
                    selectedPackageIds.filter((id) => id === pkg.id).length
                  }
                  max={pkg.maxItem}
                />
              );
            })}
          </View>
        </View>
      )}
    </View>
  );
};
